/**
 * Add to Cart Tool for CopilotKit
 *
 * Enables AI to add products to the storefront cart with approval and conflict resolution.
 */

import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useHumanInTheLoop } from '@copilotkit/react-core';
import { useNavigate } from 'react-router-dom';
import { type Country } from '@electricjs/arc';
import { useStorefront } from '@/components/Storefront/StorefrontProvider';
import { useLazyGetProductQuery } from '@/redux/slices/productApiSlice';
import { useLazyGetEmployeeByIdQuery } from '@/redux/slices/employeeApiSlice';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import { useGetOrganizationAdditionalInfoQuery } from '@/redux/slices/organizationApiSlice';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { AddToCartToolRenderer } from './AddToCartToolRenderer';
import {
  createCatalogCartEntry,
  filterOutWarranties,
  extractWarrantyMappings,
} from '@/components/Storefront/utils';
import { transformEmployeesToOptions } from '@/components/People/transformOptions';
import { useIsAdpWfnCanadaConnectedOrg } from '@/hooks/useIsAdpWfnCanadaConnectedOrg';
import { getShoppingCountryOptions } from '@/components/Storefront/components/ShoppingCountrySelector';
import { useAppDispatch } from '@/redux/store';
import {
  setShoppingCountry,
  OTHER_SHOPPING_COUNTRY,
} from '@/redux/slices/storefrontContextSlice';
import {
  CONFLICT_RESOLUTION,
  ConflictResolution,
  TOOL_NAMES,
} from '@/helpers/aiChat/tools';

/**
 * Registers CopilotKit add to cart tool. Call in components where cart operations are needed.
 *
 * @example
 * function ChatPage() {
 *   useAddToCartTool();
 *   return <CopilotChat />;
 * }
 *
 * @param options.enabled - When false, the tool stays registered (stable hook
 * call order) but is hidden from the LLM via CopilotKit's `available: 'disabled'`.
 * // TODO(TF-677): remove once gigawatt_setup_assistant entitlement ships and this
 * // is no longer needed to scope setup-assistant-only orgs' tool access.
 */
export const useAddToCartTool = ({
  enabled = true,
}: { enabled?: boolean } = {}) => {
  const { i18n } = useTranslation();
  const isAdpWfnCanada = useIsAdpWfnCanadaConnectedOrg();
  const navigate = useNavigate();
  const organizationId = useGetOrganizationId();
  const {
    warrantyUpgradePreference: warrantyUpgradePreferenceEnabled,
    storefrontInternational,
  } = useFlags();

  // Storefront context
  const {
    catalogCart,
    setCatalogCart,
    storefrontCheckoutForm,
    otherCountryFlowFirstStepForm,
    resetStorefront,
  } = useStorefront();

  // Mirror the resolved shopping country into Redux so it can be forwarded to
  // the backend agent via agentState (see useAgentState.ts). This is a
  // read-only signal — it doesn't drive any mutation here — meant to ground
  // whatever product knowledge the agent has in the same catalog a human
  // would see (CatalogStore.tsx scopes products by this same country).
  // Watching via the form (rather than e.g. StorefrontProvider) keeps this
  // entirely within Gigawatt's own files; it still picks up every change
  // regardless of whether it came from the page's selector or from this
  // tool's own setValue calls below, since they share the same live form.
  const dispatch = useAppDispatch();
  const shoppingCountryValue = storefrontCheckoutForm?.watch(
    'shoppingCountry.value'
  );
  useEffect(() => {
    dispatch(setShoppingCountry(shoppingCountryValue));
  }, [dispatch, shoppingCountryValue]);

  // API hooks
  const [getProduct] = useLazyGetProductQuery();
  const [getEmployeeById] = useLazyGetEmployeeByIdQuery();

  // Organization warranty preferences
  const { data: orgAdditionalInfo } = useGetOrganizationAdditionalInfoQuery(
    { organizationId: organizationId ?? '' },
    { skip: !organizationId }
  );

  // Keep refs up-to-date so handleAddToCart and the render callback always read
  // latest values without those values being in the dependency arrays
  const catalogCartRef = useRef(catalogCart);
  catalogCartRef.current = catalogCart;

  const storefrontCheckoutFormRef = useRef(storefrontCheckoutForm);
  storefrontCheckoutFormRef.current = storefrontCheckoutForm;

  const otherCountryFlowFirstStepFormRef = useRef(
    otherCountryFlowFirstStepForm
  );
  otherCountryFlowFirstStepFormRef.current = otherCountryFlowFirstStepForm;

  /**
   * Executes the add to cart operation
   */
  const handleAddToCart = useCallback(
    async (
      employeeId: string,
      productIds: string[],
      conflictResolution?: ConflictResolution,
      // Only ever passed from an explicit "ship to a US/Canada address"
      // button click (see AddToCartToolRenderer, one button per supported
      // domestic option) — never from the AI itself, so a human always
      // chooses the concrete destination country, not the model.
      forceDomesticCountry?: Country
    ) => {
      try {
        // 1. Handle conflict resolution
        const currentEmployee =
          storefrontCheckoutFormRef.current?.getValues('employee');

        // Determine if we should clear the cart based on conflict resolution
        const shouldClearCart =
          conflictResolution === CONFLICT_RESOLUTION.CLEAR_CART;

        if (shouldClearCart) {
          // Clear the cart completely - this also resets the employee
          resetStorefront({ keepQueryParams: true });
        }

        // 2. Set the employee in the form if needed.
        //    add_to_existing keeps the current employee — products are appended
        //    to the existing cart without changing ownership.
        //    In all other cases, set to the requested employee (or always set
        //    after a clear_cart since the form was just reset).
        const isAddToExisting =
          conflictResolution === CONFLICT_RESOLUTION.ADD_TO_EXISTING;

        // Tracks whichever employee ends up owning this cart — either the
        // freshly fetched one, or the one already in the form — so shoppingCountry
        // can always be re-derived from its address below, instead of trusting
        // whatever shoppingCountry value happens to already be in the form.
        let targetEmployee = currentEmployee;

        if (
          !isAddToExisting &&
          (shouldClearCart ||
            !currentEmployee?.value ||
            currentEmployee.value !== employeeId)
        ) {
          const employee = await getEmployeeById({
            employeeId,
            organizationId: organizationId ?? '',
          }).unwrap();

          const employeeOption = transformEmployeesToOptions(
            [employee],
            i18n.language,
            isAdpWfnCanada
          )[0];
          if (!employeeOption) {
            throw new Error(
              'Could not load employee information. Please try again.'
            );
          }
          storefrontCheckoutFormRef.current?.setValue(
            'employee',
            employeeOption
          );
          targetEmployee = employeeOption;
        }

        // Keep shoppingCountry in sync with the target employee's address,
        // mirroring the manual employee-selector behavior (EmployeeSection.tsx).
        // Always re-derived here (rather than only when a new employee was just
        // fetched above) so a stale shoppingCountry left over from a previous
        // employee/session can't let products for an international employee
        // slip into a catalog cart that has no UI once the international
        // ("other") flow takes over.
        //
        // Important: storefrontCheckoutForm is the SAME live form the actual
        // Storefront page renders from, not a private draft for this tool. So
        // we only commit the resolved country here when it's an unambiguous
        // supported catalog country — committing 'other' immediately would
        // flip the real page to the international quote flow before the user
        // has actually chosen that. The 'other' case is decided below: either
        // the blocking throw (leaves the form untouched) or an explicit
        // forceDomestic override from a button click.
        let shoppingCountry =
          storefrontCheckoutFormRef.current?.getValues('shoppingCountry');
        if (storefrontInternational && targetEmployee?.address?.country) {
          shoppingCountry =
            getShoppingCountryOptions().find(
              option => option.value === targetEmployee?.address?.country
            ) ??
            getShoppingCountryOptions().find(
              option => option.value === OTHER_SHOPPING_COUNTRY
            );
          if (
            shoppingCountry &&
            shoppingCountry.value !== OTHER_SHOPPING_COUNTRY
          ) {
            storefrontCheckoutFormRef.current?.setValue(
              'shoppingCountry',
              shoppingCountry
            );
          }
        }

        // 3. If no products specified, this is an employee-only swap - we're done
        if (productIds.length === 0) {
          return;
        }

        // The "other countries" flow is a manual quote request (single hardware
        // pick + recipient form) with no catalog cart UI — adding to catalogCart
        // here would succeed silently but never be visible to the user.
        if (shoppingCountry?.value === OTHER_SHOPPING_COUNTRY) {
          if (!forceDomesticCountry) {
            throw new Error(
              "This employee's address is outside the standard catalog countries. Ask the user whether to ship a domestic order to a US or Canada address instead, or use the international quote flow on the Storefront page to ship to the employee's actual address."
            );
          }

          // The user explicitly chose (via button click) which domestic
          // catalog to ship from despite the employee's address being
          // international — override back to that country, mirroring
          // picking it in the selector. Falls back to the default domestic
          // option only in the defensive case where the requested code
          // somehow isn't a currently-supported option.
          shoppingCountry =
            getShoppingCountryOptions().find(
              option => option.value === forceDomesticCountry
            ) ?? getShoppingCountryOptions()[0];
          storefrontCheckoutFormRef.current?.setValue(
            'shoppingCountry',
            shoppingCountry
          );
        }

        // 4. Fetch all products with error handling for partial failures
        //
        // Known gap: getProduct(id) has no country awareness (unlike the
        // catalog list/suggestion endpoints CatalogStore.tsx uses, which take
        // country_code/product_origin_name), and the returned Product never
        // carries its own country/origin — so there's no cheap way to verify
        // a given ID is actually eligible for `shoppingCountry` (e.g. a
        // US-only SKU could still end up in a Canadian employee's cart if the
        // AI passes a stale/incorrect ID). This is a pre-existing gap (plain
        // US employees were never validated either) — closing it properly
        // needs a backend change (country-aware single-product lookup, or a
        // server-side rejection at checkout). The `storefrontShoppingCountry`
        // mirrored into agentState above at least grounds the AI's own
        // product knowledge in the right catalog for the common case.
        const productPromises = productIds.map(productId =>
          getProduct(productId, true)
            .unwrap()
            .catch(error => {
              console.error(
                `[useAddToCartTool] Failed to fetch product ${productId}:`,
                error
              );
              return null;
            })
        );
        const productResults = await Promise.all(productPromises);
        const products = productResults.filter(
          (p): p is NonNullable<typeof p> => p !== null
        );

        // Throw if all fetches failed — partial failures are tolerated with a warning
        if (products.length === 0) {
          throw new Error('Failed to add products to cart. Please try again.');
        } else if (products.length !== productIds.length) {
          const failedCount = productIds.length - products.length;
          console.warn(
            `[useAddToCartTool] Failed to fetch ${failedCount} of ${productIds.length} products`
          );
        }

        // 5. Extract warranty mappings from AI-provided IDs
        const warrantyMap = extractWarrantyMappings(products, productIds);

        // 6. Filter out standalone warranty products and create cart entries
        const productsToAdd = filterOutWarranties(products);
        const newCartEntries = productsToAdd.map(product =>
          createCatalogCartEntry(product, {
            explicitWarrantyId: warrantyMap.get(product.id),
            shouldUpgradeWarranty:
              orgAdditionalInfo?.warrantyUpgradePreference &&
              warrantyUpgradePreferenceEnabled,
          })
        );

        // 7. Update cart state
        // If cart was cleared (clear_cart), use only new entries
        // Otherwise, append to existing items
        setCatalogCart(
          shouldClearCart
            ? newCartEntries
            : [...catalogCartRef.current, ...newCartEntries]
        );

        // Return the number of cart entries added (warranties are mapped onto
        // their parent product, not added as separate entries)
        return productsToAdd.length;
      } catch (error) {
        console.error(
          '[useAddToCartTool] Error adding products to cart:',
          error
        );
        throw error;
      }
    },
    [
      setCatalogCart,
      getEmployeeById,
      organizationId,
      getProduct,
      resetStorefront,
      orgAdditionalInfo?.warrantyUpgradePreference,
      warrantyUpgradePreferenceEnabled,
      storefrontInternational,
      i18n.language,
      isAdpWfnCanada,
    ]
  );

  const renderAddToCartTool = useCallback(
    (props: {
      args: Record<string, unknown>;
      status: string;
      respond?: (result: string) => void;
    }) => (
      <AddToCartToolRenderer
        {...props}
        navigate={navigate}
        catalogCart={catalogCartRef.current}
        storefrontCheckoutForm={storefrontCheckoutFormRef.current}
        otherCountryFlowFirstStepForm={otherCountryFlowFirstStepFormRef.current}
        handleAddToCart={handleAddToCart}
      />
    ),
    [navigate, handleAddToCart]
  );

  // Note: conflict_resolution (and, for the same reason, force_domestic for the
  // international-shipping choice) is intentionally not a tool parameter.
  // CopilotKit streams args while status is 'inProgress' and finalizes them at
  // the moment status becomes 'executing'. If either were a parameter, the AI
  // would include it in its generated tool call — arriving at execution time and
  // silently bypassing the conflict/choice UI entirely. Instead, the renderer
  // detects these cases itself (comparing the requested employee against the
  // current cart, or against its address on file) and the user always resolves
  // them via explicit button clicks.
  useHumanInTheLoop({
    name: TOOL_NAMES.ADD_PRODUCTS_TO_CART,
    description:
      "Adds products to the storefront cart for a specific employee. Requires user approval before adding products. Use this when the user wants to add products to their cart or create a quote. If there are existing cart items for a different employee, the user will be shown options to resolve the conflict — do not attempt to resolve it automatically. If you already know the employee's address is outside the US/Canada, say so as part of this message (before or alongside calling this tool) so the user understands why they'll be shown a shipping choice — don't wait until after they've chosen to explain it.",
    parameters: [
      {
        name: 'employee_id',
        type: 'string',
        description: 'The ID of the employee to add products for',
        required: true,
      },
      {
        name: 'product_ids',
        type: 'string[]',
        description: 'Array of product IDs to add to cart',
        required: true,
      },
    ],
    render: renderAddToCartTool,
    available: enabled ? 'enabled' : 'disabled',
  });
};
